import React, { useEffect, useMemo, useState } from 'react'
import { useViewStore } from '../../Contexts/ViewContext'
import { useLocaleStore } from '../../Contexts/LocaleContext'

import type { NamedListElementType } from '../NamedListAttachment'

import { ArrowLeftIcon, ArrowRightIcon } from '../../Icons/ArrowIcons'

interface SearchBarProps {
  elements: Array<NamedListElementType>
  elementsByCard: number
  carouselCardWidth: number
  setCardToShow: (index: number) => void
}

const remToPx = parseFloat(getComputedStyle(document.documentElement).fontSize)
// Width of one item in the search bar plus the gap between items, smaller in mobile mode
const itemWidthForMobile = 2.5 * remToPx
const itemWidthForLarge = 3.5 * remToPx

const SearchBar = ({
  elements,
  elementsByCard,
  carouselCardWidth,
  setCardToShow
}: SearchBarProps): JSX.Element => {
  const locale = useLocaleStore((state) => state.locale)
  const themeColors = useViewStore((state) => state.themeColors)
  const isMobile = useViewStore((state) => state.isMobile)
  const [firstOfEachLetter, setFirstOfEachLetter] = useState<
    Map<string, number>
  >(new Map())
  const [initialsArray, setInitialsArray] = useState<Array<string>>([])
  const [paginatedInitialsArray, setPaginatedInitialsArray] = useState<
    Array<Array<string>>
  >([])
  const [paginationIndex, setPaginationIndex] = useState<number>(0)
  const [selectedInitial, setSelectedInitial] = useState<string | undefined>(
    undefined
  )

  const currentPaginatedInitialsArray = useMemo<Array<string>>(() => {
    return paginatedInitialsArray[paginationIndex]
  }, [paginatedInitialsArray, paginationIndex])

  useEffect(() => {
    const tempFirstOfEachLetter: Map<string, number> = new Map()
    const tempInitialsArray: Array<string> = []

    for (let i = 0; i < elements.length; i++) {
      const initial = elements[i].group?.toLocaleUpperCase(locale)
      if (initial && tempFirstOfEachLetter.get(initial) === undefined) {
        tempFirstOfEachLetter.set(initial, Math.floor(i / elementsByCard))
        tempInitialsArray.push(initial)
      }
    }

    setFirstOfEachLetter(tempFirstOfEachLetter)
    setInitialsArray(tempInitialsArray)
  }, [elements])

  useEffect(() => {
    if (initialsArray.length && carouselCardWidth) {
      const nbMaxElements =
        carouselCardWidth / (isMobile ? itemWidthForMobile : itemWidthForLarge)
      if (nbMaxElements < initialsArray.length) {
        const tempArray: Array<Array<string>> = []
        // Remove 2 from the length for previous / next items
        for (let i = 0; i < initialsArray.length; i += nbMaxElements - 2) {
          tempArray.push(
            initialsArray.slice(
              i,
              Math.min(i + nbMaxElements - 2, initialsArray.length)
            )
          )
        }
        setPaginatedInitialsArray(tempArray)
      } else {
        setPaginatedInitialsArray([initialsArray])
      }
    }
  }, [initialsArray, carouselCardWidth, isMobile])

  useEffect(() => {
    setCardToShow(
      selectedInitial ? firstOfEachLetter.get(selectedInitial) || 0 : 0
    )
  }, [selectedInitial])

  const handleChangePagination = (getBack: boolean): void => {
    getBack
      ? setPaginationIndex((value) => value - 1)
      : setPaginationIndex((value) => value + 1)
  }

  return (
    <div className='rf-w-full rf-h-7 large:rf-h-10 rf-px-4 rf-flex rf-flex-row rf-justify-center rf-items-center rf-gap-3'>
      {/* Previous item */}
      {paginatedInitialsArray.length > 1 && (
        <div
          className={`rf-h-full rf-aspect-square rf-flex rf-justify-center rf-items-center rf-bg-cardFrameBackground rf-border rf-rounded ${
            paginationIndex === 0
              ? 'rf-border-lightgray'
              : 'rf-border-cardButtonBorderDefault'
          }`}
          onClick={
            paginationIndex === 0
              ? () => {}
              : () => handleChangePagination(true)
          }
        >
          <ArrowLeftIcon
            className='rf-h-5 large:rf-h-6'
            color={
              paginationIndex === 0
                ? '#818181'
                : themeColors.card.button.text.default
            }
          />
        </div>
      )}

      {/* Current list of initials */}
      {currentPaginatedInitialsArray?.map((value, key) => {
        return (
          <button
            key={key}
            className={`rf-h-full rf-aspect-square rf-flex rf-justify-center rf-items-center ${
              selectedInitial === value
                ? 'rf-card-button-mini-selected'
                : 'rf-card-button-mini'
            }`}
            onClick={() =>
              setSelectedInitial(selectedInitial === value ? undefined : value)
            }
          >
            {value}
          </button>
        )
      })}

      {/* Next item */}
      {paginatedInitialsArray.length > 1 && (
        <div
          className={`rf-h-full rf-aspect-square rf-flex rf-justify-center rf-items-center rf-bg-cardFrameBackground rf-border rf-rounded ${
            paginationIndex === paginatedInitialsArray.length - 1
              ? 'rf-border-lightgray'
              : 'rf-border-cardButtonBorderDefault'
          }`}
          onClick={
            paginationIndex === paginatedInitialsArray.length - 1
              ? () => {}
              : () => handleChangePagination(false)
          }
        >
          <ArrowRightIcon
            className='rf-h-5 large:rf-h-6'
            color={
              paginationIndex === paginatedInitialsArray.length - 1
                ? '#818181'
                : themeColors.card.button.text.default
            }
          />
        </div>
      )}
    </div>
  )
}

export default SearchBar
